import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  getPhoenixApiTenantUrl,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  ServiceProviderListDto,
  ServiceProviderWithStatementDescriptorDto,
  ServiceProviderStatusCombined,
} from '@pflegenavi/shared/api';
import { PaginatedResultSet } from '@pflegenavi/shared/utils';

interface PhoenixServiceProviderResponse {
  id: string;
  name: string;
  alternate_names: string[];
  email: string;
  global_service_provider_id: string | null;
  combined_status: ServiceProviderStatusCombined;
  receipt_type: {
    id: string;
    name: string;
  };
}

interface PhoenixPaginatedResponse {
  data: PhoenixServiceProviderResponse[];
  meta: {
    page_size: number;
    current_page: number;
    total_pages: number;
    total_count: number;
  };
}

export interface IServiceProviderPhoenixApi extends Api {
  inviteServiceProvider(params: {
    serviceProviderId: string;
    email: string;
  }): Promise<{ success: boolean }>;
  revokeServiceProviderInvite(params: {
    serviceProviderId: string;
  }): Promise<{ data: ServiceProviderWithStatementDescriptorDto }>;
  getServiceProvider(
    serviceProviderId: string
  ): Promise<{ data: ServiceProviderWithStatementDescriptorDto }>;
  updateServiceProviderStatementDescriptor(params: {
    serviceProviderId: string;
    data: { statement_descriptor: string };
  }): Promise<{ success: boolean }>;
  listServiceProviders(params: {
    nursingHomeId: string;
    page: number;
    pageSize: number;
    receiptTypeId?: string;
  }): Promise<PaginatedResultSet<ServiceProviderListDto>>;
}

export class ServiceProviderPhoenixApi implements IServiceProviderPhoenixApi {
  private readonly baseUrl: string;

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);
  }

  // eslint-disable-next-line class-methods-use-this
  get headers(): Headers {
    return new Headers({
      'content-type': 'application/json',
    });
  }

  async inviteServiceProvider({
    serviceProviderId,
    email,
  }: {
    serviceProviderId: string;
    email: string;
  }): Promise<{ success: boolean }> {
    const data = { data: { email } };
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider/${serviceProviderId}/invite`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        headers: this.headers,
      }
    );
    return result.json();
  }

  async revokeServiceProviderInvite({
    serviceProviderId,
  }: {
    serviceProviderId: string;
  }): Promise<{ data: ServiceProviderWithStatementDescriptorDto }> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider/${serviceProviderId}/revoke`,
      {
        method: 'POST',
        headers: this.headers,
      }
    );
    return result.json();
  }

  async getServiceProvider(
    serviceProviderId: string
  ): Promise<{ data: ServiceProviderWithStatementDescriptorDto }> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider/${serviceProviderId}`
    );
    return result.json();
  }

  async updateServiceProviderStatementDescriptor({
    serviceProviderId,
    data,
  }: {
    serviceProviderId: string;
    data: { statement_descriptor: string };
  }): Promise<{ success: boolean }> {
    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider/${serviceProviderId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ data }),
        headers: this.headers,
      }
    );
    return result.json();
  }

  async listServiceProviders({
    nursingHomeId,
    page,
    pageSize,
    receiptTypeId,
  }: {
    nursingHomeId: string;
    page: number;
    pageSize: number;
    receiptTypeId?: string;
  }): Promise<PaginatedResultSet<ServiceProviderListDto>> {
    // Build Flop filters
    const filters: Array<{ field: string; op: string; value: string }> = [
      { field: 'nursing_home_id', op: '==', value: nursingHomeId },
    ];

    if (receiptTypeId) {
      filters.push({
        field: 'receipt_type_id',
        op: '==',
        value: receiptTypeId,
      });
    }

    // Convert to query params
    const params = new URLSearchParams();
    params.append('page', (page + 1).toString()); // Convert 0-based to 1-based
    params.append('page_size', pageSize.toString());

    // Add filters as array params with indexed notation for proper Phoenix/Plug parsing
    filters.forEach((filter, index) => {
      params.append(`filters[${index}][field]`, filter.field);
      params.append(`filters[${index}][op]`, filter.op);
      params.append(`filters[${index}][value]`, filter.value);
    });

    const result = await superFetch(
      this.authContext,
      `${this.baseUrl}/service-provider?${params.toString()}`
    );

    const json = (await result.json()) as PhoenixPaginatedResponse;

    // Map snake_case to camelCase
    const data = json.data.map((sp) => ({
      id: sp.id,
      name: sp.name,
      alternateNames: sp.alternate_names || [],
      email: sp.email,
      globalServiceProviderId: sp.global_service_provider_id ?? undefined,
      combinedStatus: sp.combined_status,
      receiptType: {
        id: sp.receipt_type.id,
        name: sp.receipt_type.name,
      },
    }));

    return new PaginatedResultSet(
      data,
      json.meta.total_count,
      json.meta.page_size,
      json.meta.current_page - 1 // Convert back to 0-based
    );
  }
}
